import { Spinner } from "@/components/ui/spinner";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "./cn.js";
import { FileGlyph, FolderGlyph } from "./ide-file-icons.js";
import { prettySource } from "./pretty-markup.js";

type SourceListOk = {
	ok: true;
	files: string[];
	defaultPath: string | null;
};

type TreeNode =
	| { kind: "dir"; name: string; path: string; children: TreeNode[] }
	| { kind: "file"; name: string; path: string };

function languageForPath(path: string): string {
	const ext = path.split(".").pop()?.toLowerCase() ?? "";
	switch (ext) {
		case "astro":
			return "astro";
		case "tsx":
			return "tsx";
		case "ts":
			return "ts";
		case "jsx":
			return "jsx";
		case "js":
		case "mjs":
		case "cjs":
			return "js";
		case "css":
		case "scss":
			return "css";
		case "html":
			return "html";
		case "md":
		case "mdx":
			return "md";
		case "json":
			return "json";
		case "svg":
			return "xml";
		default:
			return "text";
	}
}

function mediaKindForPath(
	path: string,
): "image" | "svg" | "font" | "code" {
	const ext = path.split(".").pop()?.toLowerCase() ?? "";
	switch (ext) {
		case "svg":
			return "svg";
		case "png":
		case "jpg":
		case "jpeg":
		case "gif":
		case "webp":
		case "ico":
		case "avif":
			return "image";
		case "woff":
		case "woff2":
		case "ttf":
		case "otf":
			return "font";
		default:
			return "code";
	}
}

function sourceRawUrl(siteId: string, path: string): string {
	return `/api/sites/${siteId}/source/raw?path=${encodeURIComponent(path)}`;
}

function buildFileTree(paths: string[]): TreeNode[] {
	const root: TreeNode[] = [];
	const dirs = new Map<string, TreeNode & { kind: "dir" }>();

	const ensureDir = (path: string, name: string): TreeNode & { kind: "dir" } => {
		const existing = dirs.get(path);
		if (existing) return existing;
		const node: TreeNode & { kind: "dir" } = {
			kind: "dir",
			name,
			path,
			children: [],
		};
		dirs.set(path, node);
		const parentPath = path.includes("/")
			? path.slice(0, path.lastIndexOf("/"))
			: "";
		if (!parentPath) {
			root.push(node);
		} else {
			const parentName = parentPath.split("/").pop() ?? parentPath;
			ensureDir(parentPath, parentName).children.push(node);
		}
		return node;
	};

	for (const path of paths) {
		const parts = path.split("/").filter(Boolean);
		if (parts.length === 0) continue;
		if (parts.length === 1) {
			root.push({ kind: "file", name: parts[0]!, path });
			continue;
		}
		let acc = "";
		for (let i = 0; i < parts.length - 1; i++) {
			acc = acc ? `${acc}/${parts[i]}` : parts[i]!;
			ensureDir(acc, parts[i]!);
		}
		const parent = dirs.get(acc);
		const name = parts[parts.length - 1]!;
		parent?.children.push({ kind: "file", name, path });
	}

	const sortNodes = (nodes: TreeNode[]): TreeNode[] => {
		nodes.sort((a, b) => {
			if (a.kind !== b.kind) return a.kind === "dir" ? -1 : 1;
			return a.name.localeCompare(b.name);
		});
		for (const node of nodes) {
			if (node.kind === "dir") sortNodes(node.children);
		}
		return nodes;
	};

	return sortNodes(root);
}

function ancestorDirs(path: string): string[] {
	const parts = path.split("/").filter(Boolean);
	const dirs: string[] = [];
	let acc = "";
	for (let i = 0; i < parts.length - 1; i++) {
		acc = acc ? `${acc}/${parts[i]}` : parts[i]!;
		dirs.push(acc);
	}
	return dirs;
}

function defaultOpenDirs(selected: string | null): Set<string> {
	const open = new Set<string>(["src", "src/pages", "src/layouts", "src/components", "public"]);
	if (selected) {
		for (const dir of ancestorDirs(selected)) open.add(dir);
	}
	return open;
}

function tokenizeLine(line: string): Array<{ cls: string; text: string }> {
	if (!line) return [{ cls: "", text: " " }];
	const tokens: Array<{ cls: string; text: string }> = [];
	const re =
		/(\/\/.*$|#\s.*$|<!--.*?-->|\/\*[\s\S]*?\*\/|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|`(?:\\.|[^`\\])*`|<\/?\/?[\w:-]+|\{|\}|\b(?:import|export|from|const|let|var|function|return|if|else|async|await|type|interface|class|new|true|false|null|undefined)\b)/g;
	let last = 0;
	let match: RegExpExecArray | null;
	while ((match = re.exec(line)) !== null) {
		if (match.index > last) {
			tokens.push({ cls: "", text: line.slice(last, match.index) });
		}
		const chunk = match[0]!;
		const cls = chunk.startsWith("//") ||
			chunk.startsWith("#") ||
			chunk.startsWith("<!--") ||
			chunk.startsWith("/*")
			? "com"
			: chunk.startsWith("\"") || chunk.startsWith("'") || chunk.startsWith("`")
				? "str"
				: chunk.startsWith("<")
					? "tag"
					: chunk === "{" || chunk === "}"
						? "punc"
						: "kw";
		tokens.push({ cls, text: chunk });
		last = match.index + chunk.length;
	}
	if (last < line.length) tokens.push({ cls: "", text: line.slice(last) });
	return tokens;
}

function FileTree({
	nodes,
	depth,
	openDirs,
	selected,
	onToggle,
	onSelect,
}: {
	nodes: TreeNode[];
	depth: number;
	openDirs: Set<string>;
	selected: string | null;
	onToggle: (path: string) => void;
	onSelect: (path: string) => void;
}) {
	return (
		<ul className="mc-ide-tree">
			{nodes.map((node) => {
				if (node.kind === "dir") {
					const open = openDirs.has(node.path);
					return (
						<li key={node.path}>
							<button
								type="button"
								className="mc-ide-row mc-ide-row--dir"
								style={{ paddingLeft: `${8 + depth * 12}px` }}
								onClick={() => onToggle(node.path)}
								aria-expanded={open}
							>
								{open ? (
									<ChevronDownIcon className="mc-ide-chevron" />
								) : (
									<ChevronRightIcon className="mc-ide-chevron" />
								)}
								<FolderGlyph name={node.name} open={open} />
								<span className="truncate">{node.name}</span>
							</button>
							{open ? (
								<FileTree
									nodes={node.children}
									depth={depth + 1}
									openDirs={openDirs}
									selected={selected}
									onToggle={onToggle}
									onSelect={onSelect}
								/>
							) : null}
						</li>
					);
				}
				return (
					<li key={node.path}>
						<button
							type="button"
							className={cn(
								"mc-ide-row mc-ide-row--file",
								node.path === selected && "mc-ide-row--active",
							)}
							style={{ paddingLeft: `${8 + depth * 12}px` }}
							onClick={() => onSelect(node.path)}
						>
							<span className="mc-ide-chevron" aria-hidden />
							<FileGlyph name={node.name} />
							<span className="truncate">{node.name}</span>
						</button>
					</li>
				);
			})}
		</ul>
	);
}

export function SiteSourcePane({
	siteId,
	hint,
	previewUrl,
}: {
	siteId: string;
	hint: string;
	previewUrl: string;
}) {
	const [files, setFiles] = useState<string[]>([]);
	const [selected, setSelected] = useState<string | null>(null);
	const [content, setContent] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [listState, setListState] = useState<"loading" | "ready" | "empty">(
		"loading",
	);
	const [fileState, setFileState] = useState<"idle" | "loading" | "ready">(
		"idle",
	);
	const [openDirs, setOpenDirs] = useState<Set<string>>(() => defaultOpenDirs(null));
	const [mediaView, setMediaView] = useState<"preview" | "source">("preview");
	const userPickedRef = useRef(false);
	const previewUrlRef = useRef(previewUrl);
	const hasListRef = useRef(false);
	previewUrlRef.current = previewUrl;

	useEffect(() => {
		let cancelled = false;
		hasListRef.current = false;
		userPickedRef.current = false;
		const applyList = (listed: SourceListOk, initial: boolean) => {
			if (listed.files.length === 0) {
				setFiles([]);
				if (initial) {
					setSelected("Built page");
					setListState("empty");
				}
				return;
			}
			setFiles((prev) => {
				const added = listed.files.filter((file) => !prev.includes(file));
				if (!userPickedRef.current && added.length > 0) {
					const newest = added[added.length - 1] ?? listed.defaultPath;
					setSelected(newest ?? listed.files[0] ?? null);
					setOpenDirs((open) => {
						const next = new Set(open);
						for (const dir of ancestorDirs(newest ?? "")) next.add(dir);
						return next;
					});
				} else if (initial) {
					const next = listed.defaultPath ?? listed.files[0] ?? null;
					setSelected(next);
					setOpenDirs(defaultOpenDirs(next));
				}
				return listed.files;
			});
			hasListRef.current = true;
			setListState("ready");
			setError(null);
		};

		const load = async (initial: boolean) => {
			if (initial && !hasListRef.current) {
				setListState("loading");
				setError(null);
			}
			const query = new URLSearchParams({ hint });
			try {
				const response = await fetch(`/api/sites/${siteId}/source?${query}`, {
					credentials: "include",
				});
				const json: unknown = await response.json();
				if (cancelled) return;
				if (
					!json ||
					typeof json !== "object" ||
					!("ok" in json) ||
					json.ok !== true
				) {
					if (initial) {
						setError("Couldn’t load the files for this copy.");
						setListState("empty");
					}
					return;
				}
				const listed = json as SourceListOk;
				if (listed.files.length === 0 && initial) {
					setFiles([]);
					setSelected("Built page");
					setListState("empty");
					const html = await fetch(previewUrlRef.current, { credentials: "include" })
						.then((res) => (res.ok ? res.text() : ""))
						.catch(() => "");
					if (!cancelled) {
						setContent(html || "No source files on this preview yet.");
						setFileState("ready");
					}
					return;
				}
				applyList(listed, initial);
			} catch {
				if (!cancelled && initial) {
					setError("Couldn’t load the files for this copy.");
					setListState("empty");
				}
			}
		};

		void load(true);
		const timer = window.setInterval(() => void load(false), 1500);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [siteId, hint]);

	useEffect(() => {
		if (!selected || selected === "Built page") return;
		const kind = mediaKindForPath(selected);
		if (kind === "image" || kind === "font" || (kind === "svg" && mediaView === "preview")) {
			setFileState("ready");
			setError(null);
			setContent("");
			return;
		}
		let cancelled = false;
		setFileState("loading");
		const query = new URLSearchParams({ path: selected });
		void fetch(`/api/sites/${siteId}/source/file?${query}`, {
			credentials: "include",
		})
			.then(async (response) => {
				const json: unknown = await response.json();
				if (cancelled) return;
				if (
					json &&
					typeof json === "object" &&
					"ok" in json &&
					json.ok === true &&
					"content" in json &&
					typeof json.content === "string"
				) {
					setContent(json.content);
					setFileState("ready");
					setError(null);
					return;
				}
				const message =
					json &&
					typeof json === "object" &&
					"message" in json &&
					typeof json.message === "string"
						? json.message
						: "Couldn’t open that file.";
				setError(message);
				setContent("");
				setFileState("ready");
			})
			.catch(() => {
				if (!cancelled) {
					setError("Couldn’t open that file.");
					setFileState("ready");
				}
			});
		return () => {
			cancelled = true;
		};
	}, [siteId, selected, mediaView]);

	const tree = useMemo(() => buildFileTree(files), [files]);
	const crumbs = (selected && selected !== "Built page" ? selected : "").split(
		"/",
	).filter(Boolean);
	const fileName =
		selected === "Built page"
			? "index.html"
			: (selected?.split("/").pop() ?? "—");
	const kind =
		selected && selected !== "Built page"
			? mediaKindForPath(selected)
			: "code";
	const rawUrl =
		selected && selected !== "Built page"
			? sourceRawUrl(siteId, selected)
			: "";
	const showMedia =
		(kind === "image" || kind === "font" || kind === "svg") &&
		(kind !== "svg" || mediaView === "preview");
	const formatted = useMemo(
		() =>
			prettySource(
				content,
				selected && selected !== "Built page" ? selected : fileName,
			),
		[content, selected, fileName],
	);
	const lines = formatted.split("\n");
	const gutterWidth = String(Math.max(lines.length, 1)).length;

	const toggleDir = (path: string) => {
		setOpenDirs((prev) => {
			const next = new Set(prev);
			if (next.has(path)) next.delete(path);
			else next.add(path);
			return next;
		});
	};

	const selectFile = (path: string) => {
		userPickedRef.current = true;
		setMediaView("preview");
		setSelected(path);
		setOpenDirs((prev) => {
			const next = new Set(prev);
			for (const dir of ancestorDirs(path)) next.add(dir);
			return next;
		});
	};

	return (
		<div className="mc-ide">
			<aside className="mc-ide-explorer" aria-label="Explorer">
				<p className="mc-ide-explorer-label">Explorer</p>
				{listState === "loading" ? (
					<p className="mc-ide-empty">
						<Spinner className="size-3" /> Loading…
					</p>
				) : files.length === 0 ? (
					<p className="mc-ide-empty">
						{selected === "Built page" ? "Built page only" : "No source files yet"}
					</p>
				) : (
					<FileTree
						nodes={tree}
						depth={0}
						openDirs={openDirs}
						selected={selected}
						onToggle={toggleDir}
						onSelect={selectFile}
					/>
				)}
			</aside>
			<section className="mc-ide-editor" aria-label={fileName}>
				<div className="mc-ide-tabs">
					<span className="mc-ide-tab mc-ide-tab--active">
						<FileGlyph name={fileName} />
						<span className="truncate">{fileName}</span>
					</span>
					{kind === "svg" ? (
						<div className="mc-ide-media-toggle" role="group" aria-label="SVG">
							<button
								type="button"
								className={cn(
									"mc-ide-media-toggle-btn",
									mediaView === "preview" && "mc-ide-media-toggle-btn--active",
								)}
								onClick={() => setMediaView("preview")}
							>
								Preview
							</button>
							<button
								type="button"
								className={cn(
									"mc-ide-media-toggle-btn",
									mediaView === "source" && "mc-ide-media-toggle-btn--active",
								)}
								onClick={() => setMediaView("source")}
							>
								Source
							</button>
						</div>
					) : null}
				</div>
				{crumbs.length > 0 ? (
					<p className="mc-ide-crumb">
						{crumbs.map((part, index) => (
							<span key={`${part}-${index}`}>
								{index > 0 ? (
									<span className="mc-ide-crumb-sep" aria-hidden>
										›
									</span>
								) : null}
								{part}
							</span>
						))}
					</p>
				) : null}
				{fileState === "loading" ? (
					<p className="mc-ide-empty">
						<Spinner className="size-3" /> Opening…
					</p>
				) : error && !content && !showMedia ? (
					<p className="mc-ide-empty">{error}</p>
				) : showMedia && kind === "font" ? (
					<FontPreview url={rawUrl} name={fileName} />
				) : showMedia ? (
					<div className="mc-ide-media">
						<div className="mc-ide-media-frame">
							<img src={rawUrl} alt={fileName} />
						</div>
					</div>
				) : (
					<pre
						className="mc-ide-pre"
						data-lang={languageForPath(selected ?? "")}
					>
						{lines.map((line, index) => (
							<div key={index} className="mc-ide-line">
								<span
									className="mc-ide-gutter"
									style={{ minWidth: `${gutterWidth + 1}ch` }}
								>
									{index + 1}
								</span>
								<code className="mc-ide-code">
									{tokenizeLine(line).map((token, tokenIndex) => (
										<span
											key={tokenIndex}
											className={
												token.cls ? `mc-ide-tok mc-ide-tok--${token.cls}` : undefined
											}
										>
											{token.text}
										</span>
									))}
								</code>
							</div>
						))}
					</pre>
				)}
			</section>
		</div>
	);
}

function FontPreview({ url, name }: { url: string; name: string }) {
	const family = `src-${name.replace(/[^a-z0-9]+/gi, "-")}`;
	return (
		<div className="mc-ide-media mc-ide-media--font">
			<style>{`@font-face{font-family:"${family}";src:url("${url}");}`}</style>
			<div className="mc-ide-media-frame">
				<p className="mc-ide-font-sample" style={{ fontFamily: `"${family}", sans-serif` }}>
					Ag
				</p>
				<p className="mc-ide-font-line" style={{ fontFamily: `"${family}", sans-serif` }}>
					The quick brown fox jumps over the lazy dog.
				</p>
				<p className="mc-ide-font-meta">{name}</p>
			</div>
		</div>
	);
}
